import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerPayoutTransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchId, setSearchId] = useState('');
  const [searchName, setSearchName] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Transaction ID',
    'Freelancer ID',
    'Freelancer Name',
    'Transaction Type',
    'Amount',
    'Order ID',
    'Remarks',
    'Transaction Date',
  ];

  useEffect(() => {
    const fetchTransactions = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = '/employee-login';
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-payout-transactions`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setTransactions(response.data);
      } catch (err) {
        setError('Failed to fetch payout transactions.');
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [apiBaseURL]);

  const filteredData = transactions
    .map(transaction => {
      const amount = Number(transaction.amount);
      const dateObj = transaction.transaction_date ? new Date(transaction.transaction_date) : null;
      const transactionDate = dateObj
        ? dateObj.toLocaleString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })
        : 'N/A';

      return {
        transaction_id: transaction.transaction_id,
        freelancer_id: transaction.freelancer_id,
        freelancer_name: transaction.freelancer_name || 'N/A',
        transaction_type: transaction.transaction_type,
        amount: isNaN(amount) ? '0.00' : amount.toFixed(2),
        order_id: transaction.order_id || 'N/A',
        remarks: transaction.remarks || 'N/A',
        transaction_date: transactionDate,
        raw_date: dateObj,
      };
    })
    .filter(entry => {
      const matchesId =
        searchId === '' || entry.freelancer_id?.toLowerCase().includes(searchId.toLowerCase());
      const matchesName =
        searchName === '' || entry.freelancer_name?.toLowerCase().includes(searchName.toLowerCase());

      const withinDateRange =
        (!fromDate || !entry.raw_date || new Date(fromDate) <= entry.raw_date) &&
        (!toDate || !entry.raw_date || entry.raw_date <= new Date(toDate + 'T23:59:59'));

      return matchesId && matchesName && withinDateRange;
    });

  const handleDownloadExcel = () => {
    const now = new Date();

    const formattedDateForHeader = now.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const formattedDateForFileName = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    const fileName = `Freelancer_Payout_Summary_${formattedDateForFileName}.xlsx`;

    const excelHeader = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Payout Summary'],
      [`Downloaded on: ${formattedDateForHeader}`],
      [],
    ];

    const tableHeaders = columns;
    const tableData = filteredData.map(row =>
      columns.map(col => row[col.toLowerCase().replace(/ /g, '_')])
    );

    const worksheetData = [...excelHeader, tableHeaders, ...tableData];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Payout Transactions');

    XLSX.writeFile(workbook, fileName);
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, idx) => (
              <th key={idx} className="px-3 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3 text-center text-gray-500">
                No payout transaction data found.
              </td>
            </tr>
          ) : (
            data.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 whitespace-nowrap">
                    {row[col.toLowerCase().replace(/ /g, '_')]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div className="p-6">Loading payout transactions...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-50 ml-64">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-4">
        <h2 className="text-2xl font-semibold">Freelancer Payout Transactions</h2>
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download Excel
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-5">
        <input
          type="text"
          placeholder="Search by Freelancer ID"
          value={searchId}
          onChange={(e) => setSearchId(e.target.value)}
          className="border border-gray-300 px-3 py-2 rounded w-full md:w-1/3"
        />
        <input
          type="text"
          placeholder="Search by Freelancer Name"
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
          className="border border-gray-300 px-3 py-2 rounded w-full md:w-1/3"
        />
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-5">
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="border border-gray-300 px-3 py-2 rounded w-full md:w-1/4"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border border-gray-300 px-3 py-2 rounded w-full md:w-1/4"
        />
      </div>

      <Table columns={columns} data={filteredData} />
    </div>
  );
};

export default FreelancerPayoutTransactionsPage;
